import platform, { analytics, globalTime } from '@play-co/gcinstant';
import {
  configureExtensions,
  createPayloadEncoder,
} from '@play-co/gcinstant-replicant-extensions';
import startApplication, {
  AppView,
} from '@play-co/timestep-core/lib/startApplication';
import device from '@play-co/timestep-core/lib/device';
import StackView from '@play-co/timestep-core/lib/ui/StackView';
import View from '@play-co/timestep-core/lib/ui/View';
import * as loadingGroups from 'src/loadingGroups';
import { petSpeciesAssets } from 'src/loadingGroups';

import SceneManager from 'src/game/logic/SceneManager';
import PopupManager from 'src/game/logic/PopupManager';

import {
  hideLoading,
  setScreenSize,
  showLoading,
  showLocale,
} from './state/ui';

import {
  getApplicationScale,
  getSceneManager,
  isPlatformLine,
  getScreenBottom,
  getScreenDimensions,
  getScreenLeft,
  getScreenRight,
  getScreenTop,
  getUrlSearchParamValue,
  setRootView,
  setSceneManager,
  System,
  waitForAssets,
  waitForItPromise,
  getClientTimeZone,
} from 'src/lib/utils';

import { AB } from './lib/AB';
import { sendEntryFinalAnalytics } from 'src/lib/analytics';
import {
  getStrings,
  getUsableLocale,
  loadableLocales,
  loadLocale,
} from 'src/lib/i18n/i18n';
import setupUserProps from './lib/setupUserProps';

import uiConfig from './lib/ui/config';
import { initializeBitmapFonts } from './lib/bitmapFonts';
import { PerformanceAnalytics } from './lib/PerformanceAnalytics';
import {
  addSentryContext,
  captureGenericError,
  debugBreadcrumb,
} from 'src/lib/sentry';

import StateObserver from 'src/StateObserver';
import { createEmitter, createPersistentEmitter } from 'src/lib/Emitter';
import PopupMonitor from './game/logic/PopupMonitor';
import { sessionSetup } from 'src/lib/onLogin';
import { PauseSensitiveTimeouts } from 'src/game/logic/PauseSensitiveTimeouts';
import MaintenanceManager from './game/logic/MaintenanceManager';
import type { State } from './replicant/State';
import { duration } from './replicant/utils/duration';
import { initFlexBox } from '@play-co/timestep-core/flexbox';
import { Pet } from './replicant/buruburu/pet';
import { conversationSystem } from './game/conversation/conversation.system';
import { Conversation } from './replicant/conversation';
import { careSystem } from './game/care/care.system';
import { updatePrivateProfile } from './lib/profile';
import { startLaunchSequence } from './game/launch/LaunchSequence';
import { levelSystem } from './game/level/level.system';
import { getPet, setPet } from './game/pet';
import { EntityManager } from './game/logic/EntityManager';
import { PetController } from './game/pet/PetController';
import { iapActions } from './lib/iap/iap.redux';
import { Iap } from './replicant/iap';
import { ValidatePurchaseArgs } from '@play-co/replicant';
import { eventGTM, trackDebugPaymentsCatalog } from './lib/analytics/events';
import {
  importProductsCatalogToPlatform,
  checkPendingGemsPurchase,
} from './lib/iap/iap.util';
import { isWebPlayable } from './replicant/utils';
import { iapClient } from './lib/iap/iapClient';
import { notifyInvitedUserJoined } from './lib/sharing';

// https://docs.dev.gc-internal.net/gcinstant-replicant-extensions/index.html#usage
configureExtensions({
  analytics,
  gcinstant: platform,
  replicantClientPromise: StateObserver.replicantClientPromise,
  getPlatformStorage: (state: State) => state.platformStorage,
});

export default class Application extends View implements AppView {
  private _isInit = false;
  private rootView: StackView;
  sceneManager: SceneManager;
  popupManager: PopupManager;
  systems: Record<'conversation' | 'care' | 'level', System>;

  constructor(opts) {
    super(opts);

    PerformanceAnalytics.trackTimeTo('appConstructor');

    // Enable IAP for the mock platform
    if (process.env.REPLICANT_OFFLINE) {
      importProductsCatalogToPlatform();
    }

    // Run entry point for game
    this.onGameStart()
      .catch(async (error) => {
        // If an error occurs early in the initialization chain, platform stops
        // execution right away which swallows the error logs.
        // Logging before re-throwing gives us visibility into these errors:
        console.error(error);

        analytics.pushError('AppInitFailed', error);

        // We don't return this promise and this errors newer go up to sentry logger let's log error manually
        captureGenericError('AppInitFailed', error);

        await waitForItPromise(3000); // Give Amplitude client a chance to push events to server

        return Promise.reject(error);
      })
      .finally(() => {
        this._isInit = true;
      });
  }

  get isInit() {
    return this._isInit;
  }

  initApp(): Promise<void> {
    // Initialize flexbox before proceeding to UI initialization.
    return initFlexBox();
  }

  private async assignManualTests() {
    await StateObserver.invoke.assignManualAbTests();
  }

  private overrideOfflinePlayerID() {
    if (!process.env.REPLICANT_OFFLINE) {
      return;
    }

    const overriddenID = this.getOverriddenPlayerID();
    if (overriddenID) {
      platform.playerID = overriddenID;
      platform.playerName = 'Player ' + overriddenID;
    }
  }

  private getOverriddenPlayerID(): string | null {
    if (!process.env.REPLICANT_OFFLINE) {
      return null;
    }

    const param = window.location.search
      .replace(/\?/g, '')
      .split('&')
      .find((x) => x.startsWith('profile='));

    return param ? param.substr('profile='.length) : null;
  }

  createViews() {
    // create rootView
    const scale = getApplicationScale();
    this.rootView = new StackView({
      superview: this,
      // clip: true,
      x: (device.width - uiConfig.width) / 2,
      y: (device.height - uiConfig.height) / 2,
      width: uiConfig.width,
      height: uiConfig.height,
      scale,
      anchorX: uiConfig.width / 2,
      anchorY: uiConfig.height / 2,
      infinite: true,
      canHandleEvents: false,
    });

    setRootView(this.rootView);

    // create scene manager
    this.sceneManager = new SceneManager({ app: this });

    // Save scene manager to global var
    setSceneManager(this.sceneManager);

    // create popup manager
    this.popupManager = new PopupManager({ app: this });

    // resize
    device.screen.on('Resize', () => this.onResize());
    this.onResize();
  }

  /**
   *  Create initial states for local pet.
   */
  private initializeLocalPet() {
    const petController = new PetController(
      getSceneManager(),
      EntityManager.createOrGetPlayerPet(),
    );
    setPet(petController);
    petController.init();
  }

  private createSystems() {
    this.systems = {
      care: careSystem(this),
      conversation: conversationSystem(Conversation),
      level: levelSystem(),
    };

    createPersistentEmitter(
      ({ user }) => Pet.getters(user).getCurrentEvolution()?.species,
    ).addListener(async (species) => {
      const { pet, ui, user } = StateObserver.getState();

      getPet().appearance.setAttributes({ name: user.pet.name });

      if (
        !species ||
        pet[platform.playerID].action === 'evolve' ||
        ui.isPetCrateOpening
      ) {
        return;
      }

      await waitForAssets(petSpeciesAssets[species]);

      getPet().appearance.setSpecies(species);
    });
    createPersistentEmitter(({ user }) =>
      // Sync status effects once per second
      Pet.getters(user).getAllStatusEffects(
        Math.floor(StateObserver.now() / 1000) * 1000,
      ),
    ).addListener((statusEffects) => {
      getPet().appearance.setStatusEffects(statusEffects);
    });
    createPersistentEmitter(({ user }) => {
      // Sync attributes once per second
      const now = Math.floor(StateObserver.now() / 1000) * 1000;
      return {
        ...Pet.getters(user).getAllAttributeValues(now),
        age: Pet.getters(user).getPetAge(now),
      };
    }).addListener((attributes) => {
      const attributeState = StateObserver.getState().pet[platform.playerID]
        .attributes;
      if (attributeState && !attributeState.isUpdating) {
        getPet().appearance.setAttributes(attributes);
      }
    });
  }

  getRootView() {
    return this.rootView;
  }

  /**
   * Game start callback
   */
  private async onGameStart() {
    // Track loading progress counter
    let loadingProgress = 0;

    // Set storage adapter
    // We use custom storage adapter because createStorageAdapter store data in gcinstant.platformStorage
    // We already storing it in state.platformStorage, migration won't work for this case: https://app.circleci.com/pipelines/github/play-co/buruburu/1113/workflows/6122f89e-8456-4266-a34b-6a19e3300f7e/jobs/5943?invite=true#step-109-7
    platform.storage.setStorageAdapter({
      load: async () => {
        return StateObserver.getState().user.platformStorage;
      },
      save: async (data) => {
        // Flushing is required for accurate analytics
        await StateObserver.invoke.setPlatformStorage(data);
        await StateObserver.replicant.flush();
      },
    });

    // All payload from social API(ShareAsync, InviteAsync, tournamentShareAsync, tournamentCreateAsync)
    // will be processed by this encoder and decoder this means that instead of sending whole ab tests and other analytics related data
    // we will send the only key and save payload by this key our key/value storage, on decode will get real payload from DB by this key
    // this was done because payload limits on platforms side
    await platform.setDataCodec(
      createPayloadEncoder(() => StateObserver.replicant, analytics),
    );

    // Init platform
    await platform.initializeAsync();

    // Track initialization start
    PerformanceAnalytics.trackTimeTo('initializeAsync');

    // Add sentry metadata
    addSentryContext();

    // Show fake progress
    const loadingInterval = window.setInterval(() => {
      loadingProgress += 1;
      platform.setLoadingProgress(loadingProgress);
    }, 50);

    // Preload affinity-sorted friends if available
    if (isPlatformLine(platform)) {
      void platform.getAffinitySortedFriendIdsAsync();
    }

    // Load asset and replicant in parallel
    await Promise.all([this.onAssetLoaded(), this.onReplicantReady()]);

    // Update player node in social graph - not needed for open web
    // const socialGraph = this.updateSocialGraph();

    // Set analytics user properties
    setupUserProps();

    // Resolve early entry sequence before `startGameAsync`.
    // Resolves immediately if early entry sequence is unavailable.
    await platform.waitForEarlyEntryData();

    // Set platform Line UI translations
    if (isPlatformLine(platform)) {
      platform.setUIStrings({
        compliance: getStrings().platform.line.compliance,
      });
    }

    // Make sure progress bar is filled
    const progressBarFill = document.getElementById('progressBarFill');
    if (progressBarFill) {
      progressBarFill.style.animation = 'none';
      progressBarFill.style.width = '100%';
    }

    // Await a bit to make sure the progress bar is filled.
    await waitForItPromise(10);

    // Hide loading screen
    await platform.dismissLoadingScreenAsync();

    // Starts the game once assets are loaded and the backend is ready.
    await platform.startGameAsync();

    if (!isWebPlayable()) {
      window.Native?.target?.notifyLoaded();
    }

    PerformanceAnalytics.trackTimeTo('startGameAsync');

    // 2nd after startGameAsync()
    this.overrideOfflinePlayerID();
    addSentryContext();

    // Remove fake loading progress
    window.clearInterval(loadingInterval);

    // Resolves after entry analytics have been sent
    await platform.waitForEntryAnalytics();

    PerformanceAnalytics.trackTimeTo('entryPreChecks');
    PerformanceAnalytics.trackTimeTo('gameVisible');

    // Wait for social graph information before sending entry finale
    // await socialGraph; // not needed for open web
    await sendEntryFinalAnalytics();

    eventGTM('EntryFinal');

    PerformanceAnalytics.trackTimeTo('entryFinalEvent');

    void StateObserver.invoke.onEntryFinal();

    const { user } = StateObserver.getState();
    if (Pet.getters(user).getCurrentEvolution()) {
      await StateObserver.invoke.schedulePetActivity();
    }

    // Notify the user who shared the link that the invited player has joined the game
    void notifyInvitedUserJoined();

    // Update private profile data
    updatePrivateProfile();

    // Set the session data on the platform
    platform.setSessionData(StateObserver.getSessionData());

    PopupMonitor.init();

    this.createViews();

    this.initializeLocalPet();

    this.createSystems();

    void MaintenanceManager.init();

    Object.values(this.systems).forEach((system) => system.start());

    // Record native launch timestamp
    await this.initializeNative();

    // Switch to a scene and start opening popups.
    await startLaunchSequence();

    // Initialize performance analytics
    PerformanceAnalytics.init();

    // Update client timezone
    const tempDate = new Date();
    const timezoneOffsetMs: number = duration({
      minutes: tempDate.getTimezoneOffset(),
    });

    void StateObserver.invoke.updateUserClientSideData({
      timezoneOffsetMs: timezoneOffsetMs,
    });
  }

  /**
   * Asset loading callback
   */
  private async onAssetLoaded() {
    // detect weird bug in messenger/instagram mobile browsers
    analytics.pushEvent('DebugBrowserTimersTest');
    setTimeout(() => {
      analytics.pushEvent('DebugBrowserTimersWork');
    }, 0);

    // Warning: Due to race conditions, state is not guaranteed to be available here!
    const assetsToLoad = [loadingGroups.initialAssets.load()];

    // Loaded assets
    await Promise.all(assetsToLoad);

    // Send analytics
    PerformanceAnalytics.trackTimeTo('blockingAssets');
    PerformanceAnalytics.trackAssets();
  }

  /**
   * On replicant ready
   */
  private async onReplicantReady() {
    // Wait for replicant start
    await StateObserver.init();

    // Update user's timezone on the state right after StateObserver is initialized
    const timeZone = getClientTimeZone();
    void StateObserver.invoke.updateUserClientSideData({ timeZone });

    if (process.env.PLATFORM !== 'mock') {
      platform.setPlayerID(StateObserver.replicant.userId);
      analytics.setUserID(StateObserver.replicant.userId);
    }

    // Replicant ready event
    PerformanceAnalytics.trackTimeTo('replicantLogin');

    // Set exchange rates to be used for purchase analytics.
    platform.setExchangeRatesUSD(StateObserver.replicant.getExchangeRatesUSD());

    // Resolve platform storage before initializing A/B tests.
    // No deoptimization here as platform.loadStorage just reads from Replicant state:
    await platform.loadStorage();

    // Init AB test
    AB.initialize();
    await this.assignManualTests();

    // Various things! Do them once!
    sessionSetup();

    // Calibrate local time to match server time
    globalTime.setNow(StateObserver.now());

    // Register onPause to flush pending queues
    platform.onPause(this.onPause);

    this.initializeIap();

    // Detect and load preferred user locale
    // Also monitor local change
    await this.loadUserLocale();
  }

  private async updateSocialGraph() {
    try {
      await platform.waitForFriends();
      const nonPlayerFriendIDs = [];
      platform.socialFriends.forEach(({ id, isPlayer }) => {
        if (!isPlayer) {
          nonPlayerFriendIDs.push(id);
        }
      });

      const meta = {}; // Set any player properties that we're interested in here

      // Update state.friends for deep friends analytics
      // Platform friends should return only friends
      await StateObserver.invoke.updatePlatformFriends({
        friendIds: platform.friendIds,
      });

      // Update socialGraph
      await StateObserver.replicant.socialGraph.updateSelf(
        nonPlayerFriendIDs,
        meta,
      );

      // Friends analytics
      const friendAnalytics = await StateObserver.replicant.asyncGetters.getFriendAnalytics(
        {
          friendIds: platform.friendIds,
        },
      );

      // Set friends analytics, should be set before sending entry finale analytics
      platform.setFriendAnalytics(friendAnalytics);
    } catch (error) {
      console.warn('Updating social graph failed', error);
      captureGenericError('Updating social graph failed', error);
    }

    // ONLY FOR DEV !!!
    // This trick solve issue with production friends on dev/alpha/qa environments
    // If you use production friends on dev envs you may got issues with api.postMessage
    // when trying to send message to players that not exist on DB
    // fetchStates will create empty states for not existed user
    // See conversation: https://playcoinc.slack.com/archives/C02ALN39W/p1655847248363799
    if (process.env.IS_DEVELOPMENT) {
      await StateObserver.replicant.fetchStates([...platform.friendIds]);
    }
  }

  private initializeIap() {
    // Swap GCInstant by custom IAP client
    const platform = iapClient;

    if (!platform.paymentsAvailable) {
      return;
    }

    // initialize in app purchases
    platform
      .iapInit({ productIDs: Iap.ruleset.productIds })
      .then(() => platform.getCatalogAsync())
      .then((catalog) => {
        // Disables purchases if the catalog is empty
        StateObserver.dispatch(iapActions.setIapReady(!!catalog.length));
        trackDebugPaymentsCatalog(
          catalog.reduce((a, x) => {
            a[x.id] = x.price;
            return a;
          }, {}),
        );
      })
      .then(() => checkPendingGemsPurchase())
      .catch((e) => captureGenericError('IAP init failed', e));

    // Register onPaymentsReady() to set the payments state
    platform.onPaymentsReady(() => {
      PerformanceAnalytics.trackTimeTo('paymentsReady');
    });

    // Register onProvisionProductAsync() to handle purchases
    platform.onProvisionProductAsync(async (purchase, product) => {
      // Apple validation also requires the transactionID/token
      // Since app store receipts can contain many transactions
      const validationArgs: ValidatePurchaseArgs = {
        paymentProcessor: 'ios',
        signedRequest: purchase.signedRequest,
        transactionId: purchase.token,
        productIdPrefix: '',
        productId: product?.id ?? '',
      };

      await StateObserver.invoke.asyncHandlePurchase(validationArgs);
    });
  }

  private async initializeNative() {
    if (!platform.insideNativeIOS) {
      return;
    }

    switch (window.TLNative.Settings.os) {
      case 'android': {
        // Save the timestamp on replicant
        await StateObserver.invoke.updateGoogleLastLaunched();
        break;
      }
      default: {
        // Save the timestamp on replicant
        await StateObserver.invoke.updateAppleLastLaunched();
        break;
      }
    }
  }

  /**
   * Detect and load user locale
   */
  private async loadUserLocale() {
    // Get locale from settings
    let initialLocale = StateObserver.getState().user.settings.locale;

    // If local not set, detect it based on browser language
    if (!initialLocale || !loadableLocales[initialLocale]) {
      let locale =
        (process.env.IS_DEVELOPMENT && getUrlSearchParamValue('locale')) ||
        window.navigator.language ||
        'ja';

      locale = locale.replace('_', '-');
      if (locale.indexOf('-') !== -1) {
        locale = locale.split('-')[0];
      }

      locale = locale.toLowerCase();
      initialLocale = getUsableLocale(locale);

      // Save locale to replicant
      await StateObserver.invoke.updateSettings({
        locale: initialLocale,
      });
    }

    // Load locale fonts
    await Promise.all([
      loadLocale(initialLocale),
      initializeBitmapFonts(initialLocale),
    ]);

    // Update UI
    StateObserver.dispatch(showLocale(initialLocale));

    // Monitor the locale switch
    createEmitter(
      this.getRootView(),
      ({ user }) => user.settings.locale,
    ).addListener(
      async (lang) => this.onLocaleChange(lang),
      { noInit: true }, // Initial locale is loaded and set just above.
    );
  }

  /**
   * On locale switch
   */
  private async onLocaleChange(lang: string) {
    const locale = getUsableLocale(lang);
    StateObserver.dispatch(showLoading());

    try {
      await Promise.all([loadLocale(locale), initializeBitmapFonts(locale)]);
      StateObserver.dispatch(showLocale(locale));
    } finally {
      StateObserver.dispatch(hideLoading());
    }
  }

  private onResize() {
    // rescale rootView
    const scale = getApplicationScale();
    this.rootView.updateOpts({
      backgroundColor: 'black',
      x: (device.width - uiConfig.width) / 2,
      y: (device.height - uiConfig.height) / 2,
      scale,
    });

    // generate screen data
    const screen = getScreenDimensions();
    const screenSize = {
      width: screen.width,
      height: screen.height,
      top: getScreenTop(),
      bottom: getScreenBottom(),
      left: getScreenLeft(),
      right: getScreenRight(),
    };

    // dispatch screen info to redux
    StateObserver.dispatch(setScreenSize(screenSize));
  }

  onPause = () => {
    if (!StateObserver.replicant || StateObserver.replicant.isPaused()) {
      return;
    }

    debugBreadcrumb('Pause');

    if (this.systems) {
      Object.values(this.systems).forEach((system) => system.stop());
    }

    // Flush Replicant
    debugBreadcrumb('Replicant flush started');
    StateObserver.replicant
      .flush()
      .then(() => debugBreadcrumb('Replicant flush done'))
      .catch((e) => debugBreadcrumb('Replicant flush failed: ' + e.message));

    // Pause Replicant
    StateObserver.replicant.pause();

    PerformanceAnalytics.pause();

    PauseSensitiveTimeouts.pause();

    // Register onResume to be fired on next user interaction
    document.addEventListener('mousedown', this.onResume, true);
    document.addEventListener('touchstart', this.onResume, true);
  };

  onResume = () => {
    document.removeEventListener('mousedown', this.onResume);
    document.removeEventListener('touchstart', this.onResume);

    if (!StateObserver.replicant?.isPaused()) {
      return;
    }

    debugBreadcrumb('Resume');

    StateObserver.replicant.resume();

    // First onResume call can happen before systems are set. This might indicate a deeper issue in the app initialization.
    // However, it seems to be safe to just ignore it in case that happens, as systems are being started right after created.
    // Fixes this sentry issue: https://playco.sentry.io/issues/4144904707/?environment=production&project=4504851572260864&query=is%3Aunresolved&referrer=issue-stream&statsPeriod=14d&stream_index=6
    if (this.systems) {
      Object.values(this.systems).forEach((system) => system.start());
    }

    PerformanceAnalytics.resume();

    PauseSensitiveTimeouts.resume();
  };
}
startApplication(Application);
